<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  FormDefinition,
  FormElementBase,
  FormElementRule,
  FormElementRuleType,
  RuleConditionBase,
  RuleConditionType,
  StatusBasedConditionType,
  WorkflowDefinition,
  WorkflowStatusRuleCondition,
} from "~/api";
import { statusConditionDisplayNames } from "~/utils/enumDisplayNames";
import { showError } from "~/utils/handleAPIError";
import { isWorkflowStatusBasedCondition } from "~/utils/typeGuards";

export interface EditCondition {
  rule: FormElementRule;
  condition: RuleConditionBase;
}

const emit = defineEmits<{
  (e: "update:rules", value: FormElementRule[]): void;
  (e: "update:editing-condition", value?: EditCondition): void;
}>();

const props = defineProps<{
  form: FormDefinition;
  workflowDefinition: WorkflowDefinition;
  editingCondition?: EditCondition;
}>();

/** Adding a Rule */
const ruleConditionType = RuleConditionType.WORKFLOW_STATUS_BASED;

/** the list of form elements selected for a rule to be applied to */
const _applicableElementGuids = ref<string[]>([]);
const applicableElements = computed<FormElementBase[]>(() =>
  props.form.components.filter((c) =>
    _applicableElementGuids.value.includes(c.guid),
  ),
);

/** the workflow status which the rule depends on */
const _conditionWorkflowStatus = ref<string>();
const statusOptions = ref<{ l: string; v: string }[]>(
  props.workflowDefinition.statusList
    ?.filter((s) => s.label !== null && s.code !== null)
    // using ! because we filtered nulls above
    .map((s) => ({ l: s.label!, v: s.code! })) ?? [
    { l: "Started", v: "Started" },
    { l: "In Progress", v: "In Progress" },
    { l: "Completed", v: "Completed" },
  ],
);

/** the type of the rule (e.g. hide, readonly) */
const ruleType = ref<FormElementRuleType>();
/** the type of the condition (e.g. status is, status is not, etc.) */
const conditionType = ref<StatusBasedConditionType>(
  Object.values(StatusBasedConditionType)[0],
);

/** whether to use "and" or "or" logic for rules with multiple conditions */
const trueIfAny = ref<boolean>(true);

/** available form items */
const formItems = computed<FormElementBase[]>(() =>
  props.form.components.filter((c) => c.guid !== undefined && !c.isArchived),
);

/** 'true' if the form item is not required for the condition */
const isRuleReadyToAdd = computed<boolean>(() =>
  Boolean(
    applicableElements.value &&
      ruleType.value &&
      conditionType.value &&
      _conditionWorkflowStatus.value,
  ),
);

/**
 * add a rule to the form
 * @param elementQuestionCode the question code of the element to add the rule to
 */
const addElementRuleToForm = (
  elementQuestionCode: string,
  elementGuid: string | undefined,
  newCondition: WorkflowStatusRuleCondition,
) => {
  if (!isRuleReadyToAdd.value) {
    showError(
      "Developer Error: tried to add a rule that is missing some required fields",
    );
    return;
  }
  if (!ruleType.value || !elementQuestionCode) {
    showError(
      "Developer Error: tried to add a rule that is missing some required fields 2",
    );
    return;
  }

  // If we're editing an existing condition, find the original rule
  if (props.editingCondition) {
    // Find the original rule that contains the condition we're editing
    const originalRule = props.form.rules.find(
      (rule) => rule.guid === props.editingCondition?.rule.guid,
    );

    if (originalRule) {
      const updatedRule = {
        ...originalRule,
        elementQuestionCode: elementQuestionCode,
        elementGuid: elementGuid ?? "",
        ruleType: ruleType.value,
        trueIfAny: trueIfAny.value,
        conditions: [
          ...originalRule.conditions.filter(
            (c) => c.guid !== props.editingCondition?.condition.guid,
          ),
          newCondition,
        ],
      };

      emit("update:rules", [
        ...props.form.rules.filter((r) => r !== originalRule),
        updatedRule,
      ]);
      emit("update:editing-condition", undefined);
      return;
    }
  }

  // check if the rule already exists
  const existingRule = props.form.rules.find(
    (rule) =>
      rule.elementQuestionCode === elementQuestionCode &&
      rule.ruleType === ruleType.value,
  );

  if (existingRule) {
    // Add the condition to the existing rule
    const updatedExistingRule = {
      ...existingRule,
      conditions: [...existingRule.conditions, newCondition],
      trueIfAny: trueIfAny.value,
    };
    emit("update:rules", [
      ...props.form.rules.filter((r) => r !== existingRule),
      updatedExistingRule,
    ]);
    return;
  }

  // otherwise, create a new rule
  const newRule: FormElementRule = {
    // @ts-expect-error: Force the server to generate the Guid
    guid: undefined,
    elementQuestionCode: elementQuestionCode,
    elementGuid: elementGuid ?? "",
    ruleType: ruleType.value,
    trueIfAny: trueIfAny.value,
    conditions: [newCondition],
  };

  emit("update:rules", [...props.form.rules, newRule]);
};

const resetFormFields = () => {
  conditionType.value = StatusBasedConditionType.STATUS_IS;
  _conditionWorkflowStatus.value = undefined;
  ruleType.value = undefined;
  _applicableElementGuids.value = [];
  trueIfAny.value = true;
};

/**
 * iterates through the list of question codes selected and adds the rule to each element
 */
const handleAddRule = () => {
  if (!isRuleReadyToAdd.value || !conditionType.value) {
    showError(
      "Developer Error: tried to add a rule that is missing some required fields",
    );
    return;
  }

  const newCondition: WorkflowStatusRuleCondition = {
    // @ts-expect-error: Force the server to generate the Guid
    guid: undefined,
    conditionType: ruleConditionType,
    type: conditionType.value,
    workflowGuid: props.workflowDefinition.guid,
    workflowStatus: _conditionWorkflowStatus.value ?? statusOptions.value[0].v,
  };

  // iterate through the list of element guids selected and add the rule to each element
  applicableElements.value.forEach((el) =>
    addElementRuleToForm(el.questionCode, el.guid, newCondition),
  );
  resetFormFields();
};

watch(
  () => props.editingCondition,
  () => {
    if (
      props.editingCondition &&
      isWorkflowStatusBasedCondition(props.editingCondition.condition)
    ) {
      _conditionWorkflowStatus.value =
        props.editingCondition.condition.workflowStatus ?? undefined;
      conditionType.value = props.editingCondition.condition.type;
      ruleType.value = props.editingCondition.rule.ruleType;
      _applicableElementGuids.value = [props.editingCondition.rule.elementGuid];
      trueIfAny.value = props.editingCondition.rule.trueIfAny;
    } else {
      conditionType.value = StatusBasedConditionType.STATUS_IS;
      _conditionWorkflowStatus.value = undefined;
      ruleType.value = undefined;
      _applicableElementGuids.value = [];
      trueIfAny.value = true;
    }
  },
);
</script>

<template>
  <div class="rule-editor">
    <div>If Workflow</div>
    <el-select
      v-model="conditionType"
      size="small"
      placeholder="Condition"
      clearable
      style="max-width: 200px"
      filterable
    >
      <el-option
        v-for="condition in Object.values(StatusBasedConditionType)"
        :key="condition"
        :label="statusConditionDisplayNames[condition]"
        :value="condition"
      >
      </el-option>
    </el-select>

    <el-select
      v-model="_conditionWorkflowStatus"
      size="small"
      placeholder="Status"
      clearable
      filterable
    >
      <el-option
        v-for="kvp in statusOptions"
        :key="kvp.v"
        :label="kvp.l"
        :value="kvp.v"
      >
        {{ kvp.l }} - {{ kvp.v }}
      </el-option>
    </el-select>

    <span> Set </span>
    <el-select
      v-if="!editingCondition"
      v-model="_applicableElementGuids"
      size="small"
      placeholder="Form Item(s)"
      multiple
      collapse-tags
      collapse-tags-tooltip
      filterable
      clearable
    >
      <el-option
        v-for="item in formItems"
        :key="item.guid"
        :label="item.questionCode"
        :value="item.guid"
      >
      </el-option>
    </el-select>
    <el-select
      v-if="editingCondition"
      v-model="_applicableElementGuids"
      size="small"
      placeholder="Form Item(s)"
      collapse-tags
      collapse-tags-tooltip
      filterable
      clearable
    >
      <el-option
        v-for="item in formItems"
        :key="item.guid"
        :label="item.questionCode"
        :value="item.guid"
      >
      </el-option>
    </el-select>

    <span> to </span>

    <el-select
      v-model="ruleType"
      size="small"
      placeholder="Rule Type"
      filterable
      clearable
    >
      <el-option
        v-for="rt in Object.values(FormElementRuleType)"
        :key="rt"
        :label="rt"
        :value="rt"
      >
      </el-option>
    </el-select>

    <el-checkbox v-model="trueIfAny" size="small" label="True if any?" />
    <el-button
      v-if="!editingCondition"
      :disabled="!isRuleReadyToAdd"
      type="primary"
      @click="handleAddRule"
    >
      Add
    </el-button>
    <div v-else style="display: flex">
      <el-button
        type="primary"
        :disabled="!isRuleReadyToAdd"
        @click="handleAddRule"
      >
        Update
      </el-button>

      <el-button
        type="danger"
        plain
        @click="
          () => {
            emit('update:editing-condition', undefined);
            resetFormFields();
          }
        "
      >
        Cancel
      </el-button>
    </div>
  </div>
</template>

<style lang="less" scoped>
.rule-editor {
  display: grid;
  grid-template-columns:
    max-content repeat(2, auto)
    max-content auto max-content auto repeat(2, max-content);
  gap: 20px;
  align-items: center;
  .answer {
    height: fit-content;
  }
}
</style>
